import { BOR_SERVICE_URLS, KEYCLOAK_URLS } from './bor-api-keys.generated';

export type Region = 'EU' | 'CN';
export type EnvKey = 'dev' | 'staging' | 'prod';

export interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
}

export interface EnvConfig {
  keycloak: KeycloakConfig;
  supergodUrl: string;
  supergodOtherServerUrl: string;
  bhiveUrl: string;
  cubeUrl: string;
  /** BOR (bulk org reconciliation) service — market-research matching/search. */
  borServiceUrl: string;
}

export type AppConfigShape = Record<EnvKey, Record<Region, EnvConfig>>;

const KEYCLOAK_CLIENT_ID = 'bhive-angular';

// dev has no separate Keycloak instance and shares the non-prod ("staging") realm.
// prod is a separate instance with its own realm. EU and CN share one Keycloak today.
const KEYCLOAK_REALMS: Record<EnvKey, string> = {
  dev: 'staging',
  staging: 'staging',
  prod: 'production'
};

const keycloakFor = (envKey: EnvKey): KeycloakConfig => ({
  url: KEYCLOAK_URLS[envKey],
  realm: KEYCLOAK_REALMS[envKey],
  clientId: KEYCLOAK_CLIENT_ID
});

export const APP_CONFIG: AppConfigShape = {
  dev: {
    EU: {
      keycloak: keycloakFor('dev'),
      supergodUrl: 'http://localhost:8088/super-god-api',
      supergodOtherServerUrl: 'https://gw.test.thebhive.net/super-god-api',
      bhiveUrl: 'https://gw.test.europe.thebhive.net',
      // Relative path → proxied to localhost:8099 by proxy.conf.json (avoids CORS in dev).
      // Override via BOR_SERVICE_URL_DEV_EU in .env — see scripts/gen-bor-keys.js.
      borServiceUrl: BOR_SERVICE_URLS.dev.EU,
      cubeUrl: 'https://analytics.test.thebhive.net/cubejs-api/v1',
    },
    CN: {
      keycloak: keycloakFor('dev'),
      supergodUrl: 'https://cn.test.supergod.thebhive.cn/super-god-api',
      supergodOtherServerUrl: 'https://cn.test.supergod.thebhive.cn/super-god-api',
      bhiveUrl: 'https://gw1.test.thebhive.net',
      // Relative path → proxied to localhost:8099 by proxy.conf.json (avoids CORS in dev).
      borServiceUrl: BOR_SERVICE_URLS.dev.CN,
      cubeUrl: 'https://analytics.test.thebhive.net/cubejs-api/v1',
    }
  },
  staging: {
    EU: {
      keycloak: keycloakFor('staging'),
      supergodUrl: 'https://supergod-api.test.europe.thebhive.net/super-god-api',
      supergodOtherServerUrl: 'https://supergod-api.test.china.thebhive.net/super-god-api',
      bhiveUrl: 'https://gw.test.europe.thebhive.net',
      // Defaults to the real staging BOR host (confirmed against the backend's own
      // application-stage.properties `openapi.server.url`); override via
      // BOR_SERVICE_URL_STAGING_EU in .env — see scripts/gen-bor-keys.js.
      borServiceUrl: BOR_SERVICE_URLS.staging.EU,
      cubeUrl: 'https://analytics.test.thebhive.net/cubejs-api/v1',
    },
    CN: {
      keycloak: keycloakFor('staging'),
      supergodUrl: 'https://supergod-api.test.china.thebhive.net/super-god-api',
      supergodOtherServerUrl: 'https://supergod-api.test.europe.thebhive.net/super-god-api',
      bhiveUrl: 'https://api.test.china.thebhive.net',
      // No separate CN BOR deployment exists today — defaults to the EU host.
      // Override via BOR_SERVICE_URL_STAGING_CN in .env if that changes.
      borServiceUrl: BOR_SERVICE_URLS.staging.CN,
      cubeUrl: 'https://analytics.test.thebhive.net/cubejs-api/v1',
    }
  },
  prod: {
    EU: {
      keycloak: keycloakFor('prod'),
      supergodUrl: 'https://gw.thebhive.net/super-god-api',
      supergodOtherServerUrl: 'https://gw.thebhive.cn/super-god-api',
      bhiveUrl: 'https://gw.thebhive.net',
      // Defaults to the real prod BOR host (confirmed against the backend's own
      // application-prod.properties `openapi.server.url`); override via
      // BOR_SERVICE_URL_PROD_EU in .env — see scripts/gen-bor-keys.js.
      borServiceUrl: BOR_SERVICE_URLS.prod.EU,
      cubeUrl: 'https://analytics-v2.prod.europe.thebhive.net/cubejs-api/v1'
    },
    CN: {
      keycloak: keycloakFor('prod'),
      supergodUrl: 'https://gw.thebhive.cn/super-god-api',
      supergodOtherServerUrl: 'https://gw.thebhive.net/super-god-api',
      bhiveUrl: 'https://gw.thebhive.cn',
      // No separate CN BOR deployment exists today — defaults to the EU host.
      // Override via BOR_SERVICE_URL_PROD_CN in .env if that changes.
      borServiceUrl: BOR_SERVICE_URLS.prod.CN,
      cubeUrl: 'https://analytics-v2.prod.europe.thebhive.net/cubejs-api/v1'
    }
  }
};
